<template>
    <div class="u-full-height u-flex u-flex-col">
        <div class="mb-24">
            <h2 class="u-text-normal mb-12">
                Tags <span class="text-light">({{ tags.length }})</span>
            </h2>

            <div>
                <app-button icon="plus" size="small" @click.native="onClickAdd">Erstellen</app-button>
                <input v-model="search" class="tags__search mt-6 mt-0@md ml-6@md" type="text" placeholder="Nach Tag suchen" />
            </div>
        </div>

        <loading-overlay :active="isLoading">
            <div v-if="filteredTags.length" class="u-flex u-flex-wrap pb-24">
                <div
                    v-for="tag in filteredTags"
                    :key="tag.id"
                    class="tags__item col-12 col-md-3 mb-12 mr-12 p-18 u-flex u-flex--space-between"
                >
                    <div class="tags__item__name" @click="onClickTag(tag.name)">
                        <span
                            class="mr-3"
                            :title="tag.name !== getTruncatedTagName(tag.name) ? tag.name : undefined"
                        >{{ getTruncatedTagName(tag.name) }}</span>
                        <small
                            class="text-mute"
                            :title="`${tag.numArchivedDocuments} zugewiesene Dokumente`"
                        >({{ tag.numArchivedDocuments }})</small>
                    </div>
                    <div>
                        <button
                            class="tags__item__action u-reset-button p-6 mr-12"
                            title="Bearbeiten"
                            @click="onClickEdit(tag)"
                        >
                            <app-icon name="pencil" :scale="1.1" hidden />
                        </button>

                        <button
                            class="tags__item__action u-reset-button p-6"
                            title="Löschen"
                            @click="onClickDelete(tag)"
                        >
                            <app-icon name="trashcan" :scale="1.1" hidden />
                        </button>
                    </div>
                </div>
            </div>
            <div v-else-if="!isLoading" class="u-flex u-flex--center u-flex-grow pt-56">
                <div class="u-text-center">
                    <app-icon name="tag" :scale="4" hidden class="mb-12" />
                    <p class="text-mute">{{ emptyText }}</p>
                </div>
            </div>
        </loading-overlay>

        <confirm-modal
            v-if="showDeleteModal"
            v-model="showDeleteModal"
            headline="Löschen"
            :is-loading="isLoadingModal"
            @confirm="onDeleteTag"
        >
            <p class="m-0">Möchtest du den Tag "{{ selectedTag.name }}" wirklich löschen?<br><small>Die damit verknüpften Dokumente werden entkoppelt und bleiben erhalten.</small></p>
        </confirm-modal>

        <form-modal
            v-if="showEditModal"
            v-model="showEditModal"
            :headline="selectedTag ? 'Bearbeiten' : 'Hinzufügen'"
            confirm-label="Speichern"
            :is-loading="isLoadingModal"
            @confirm="onEditTag"
        >
            <div class="form-group mr-12">
                <label for="title">Tag Name</label>
                <input class="form-control" id="title" type="text" v-model="form.name" autofocus />
            </div>
        </form-modal>
    </div>
</template>

<script>
import AppIcon from '@/components/ui/Icon.vue'
import Button from '@/components/ui/Button.vue'
import ConfirmModal from '@/components/ui/modals/ConfirmModal.vue'
import FormModal from '@/components/ui/modals/FormModal.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'

const MAX_TAG_NAME_LENGTH = 13

export default {
    name: 'Tags',

    components: { LoadingOverlay, ConfirmModal, FormModal, AppIcon, AppButton: Button },

    data () {
        return {
            tags: [],
            search: '',
            selectedTag: null,
            showDeleteModal: false,
            showEditModal: false,
            form: {
                name: '',
            },
            isLoading: false,
            isLoadingModal: false,
        }
    },

    computed: {
        filteredTags () {
            if (!this.search) return this.tags

            const search = this.search

            return this.tags.filter(tag => tag.name.toLowerCase().includes(search.toLowerCase()))
        },

        emptyText () {
            if (!this.tags.length) {
                return 'Du hast noch keine Tags hochgeladen.'
            }

            return 'Es wurden keine Tags zu deinen Kriterien gefunden.'
        },
    },

    watch: {
        showEditModal (show) {
            if (!show) {
                this.form.name = ''
            }
        },
    },

    mounted () {
        this.loadTags()
    },

    methods: {
        async loadTags () {
            this.isLoading = true

            try {
                const response = await this.$http.get('/document-tags')
                this.tags = response.data
            } catch (e) {
                console.log(e)
                this.$toast.open({ type: 'error', message: e.message })
            } finally {
                setTimeout(() => {
                    this.isLoading = false
                }, 1000)
            }
        },

        getTruncatedTagName (name) {
            if (name.length <= MAX_TAG_NAME_LENGTH) return name

            return `${name.substring(0, MAX_TAG_NAME_LENGTH)}...`
        },

        onClickAdd () {
            this.showEditModal = true
        },

        onClickEdit (tag) {
            this.selectedTag = tag
            this.form.name = tag.name
            this.showEditModal = true
        },

        async onEditTag () {
            this.isLoadingModal = true
            await new Promise(r => setTimeout(r, 2000)) // @TODO debug

            try {
                if (!this.selectedTag) {
                    // New tag
                    const response = await this.$http.post('/document-tag/add', { name: this.form.name })
                    if (response.data?.error) {
                        throw new Error(response.data.error)
                    }

                    this.tags.unshift(response.data.entity)
                    this.$toast.open({ type: 'success', message: response.data.successHtml })
                } else {
                    const response = await this.$http.post(`/document-tag/edit/${this.selectedTag.id}`, { name: this.form.name })
                    if (response.data?.error) {
                        throw new Error(response.data.error)
                    }

                    const index = this.tags.findIndex(x => x.id === this.selectedTag.id)
                    this.tags[index] = response.data.entity

                    this.$toast.open({ type: 'success', message: response.data.successHtml })
                }

                this.selectedTag = null
                this.form.name = ''
                this.showEditModal = false
            } catch (e) {
                console.log(e)
                this.$toast.open({ type: 'error', message: e.message })
            } finally {
                this.isLoadingModal = false
            }
        },

        onClickDelete (tag) {
            this.selectedTag = tag
            this.showDeleteModal = true
        },

        async onDeleteTag () {
            this.isLoadingModal = true
            await new Promise(r => setTimeout(r, 2000)) // @TODO debug

            try {
                const index = this.tags.findIndex(x => x.id === this.selectedTag.id)
                this.tags.splice(index, 1)

                const response = await this.$http.delete(`/document-tag/delete/${this.selectedTag.id}`)
                if (response.data?.error) {
                    throw new Error(response.data.error)
                }

                this.$toast.open({ type: 'success', message: response.data.successHtml })

                this.selectedTag = null
                this.showDeleteModal = false
            } catch (e) {
                console.log(e)
                this.$toast.open({ type: 'error', message: e.message })
            } finally {
                this.isLoadingModal = false
            }
        },

        onClickTag (searchTerm) {
            this.$router.push({ name: 'Documents', query: { searchTerm } })
        },
    },
}
</script>

<style lang="scss" scoped>
$tag-height: 30px;

.tags {
    &__item {
        background: darken($tertiary-color, 5%);
        border-radius: $default-border-radius;
        min-height: $tag-height;

        @include interaction {
            background: rgba($primary-color, 0.2);
        }

        &__name {
            cursor: pointer;

            @include interaction {
                text-decoration: underline;
            }
        }

        &__action {
            border-radius: $border-radius-round;
            display: inline-flex;

            @include interaction {
                background: $highlight-color;
                color: $white;
            }
        }
    }

    &__search {
        position: relative;
        top: 1px; /* Align with button */
        height: $button-min-height-small;
        padding: $spacing-unit-6 $spacing-unit-12;
        border-width: 1px;
        border-radius: $default-border-radius;

        @include media-breakpoint-down(md) {
            width: 100%;
        }
    }
}
</style>
